/* eslint-disable max-lines */
import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { values } from "lodash";
import { Container } from "semantic-ui-react";
import { updateQuestion } from "@teamprevent/common/questionnaires/actions";
import { CustomMedicalType, MedicalTypeId } from "@teamprevent/common/healthSurveillance/models";
import { CompletedDate } from "@teamprevent/common/components/CompletedDate";
import { HAVS_TIERS } from "@teamprevent/common/healthSurveillance/models";
import { checkExistenceOfQuestionWithId } from "@teamprevent/common/questionnaires/checkExistenceOfQuestionWithId";
import { AnsweredQuestionWithId, FileAnswer, QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";

import { CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { ContextAwareProgressBarQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/ContextAwareProgressBarQuestionnaireContainer";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import * as questionIds from "@teamprevent/common/healthSurveillance/questions/questionIds";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { DidNotAttendButton } from "../../clinicalNotes/components/DidNotAttendButton";
import { ApplicationState } from "../../applicationState";
import { HavsTier as HavsTierQuestionId, HavsAssessmentRequired as HavsAssessmentRequiredQuestionId } from "../consultationQuestions/questionIds";
import { TasksApi } from "../../tasks/tasksApi";
import { sections } from "../consultation";
import { questionConfigsForConsultationSelector } from "../selectors";

import { HealthSurveillanceConsultationConfidentialitySection } from "./HealthSurveillanceConsultationConfidentialitySection";
import { HealthSurveillanceConsultationHealthQuestionsSection } from "./HealthSurveillanceConsultationHealthQuestionsSection";
import { HealthSurveillanceConsultationDrugAlcoholTestSection } from "./HealthSurveillanceDrugAlcoholTestSection";
import { HealthSurveillanceConsultationUrinalysisSection } from "./HealthSurveillanceConsultationUrinalysisSection";
import { HealthSurveillanceConsultationMsdAssessmentSection } from "./HealthSurveillanceConsultationMsdAssessmentSection";
import { HealthSurveillanceConsultationSkinAssessmentSection } from "./HealthSurveillanceConsultationSkinAssessmentSection";
import { HealthSurveillanceConsultationHavsSection } from "./HealthSurveillanceConsultationHavsSection";
import { HealthSurveillanceConsultationExaminationSection } from "./HealthSurveillanceConsultationExaminationSection";
import { HealthSurveillanceConsultationGeneralClinicNotesSection } from "./HealthSurveillanceConsultationGeneralClinicalNotesSection";
import { HealthSurveillanceConsultationAudiometrySection } from "./HealthSurveillanceConsultationAudiometrySection";
import { HealthSurveillanceConsultationSpirometrySection } from "./HealthSurveillanceConsultationSpirometrySection";
import { HealthSurveillanceConsultationVisionSection } from "./HealthSurveillanceConsultationVisionSection";
import { HealthSurveillanceConsultationBiologicalMonitoringSection } from "./HealthSurveillanceConsultationBiologicalMonitoringSection";
import { HealthSurveillanceConsultationManualHandlingMSKQuestionnaireSection } from "./HealthSurveillanceConsultationManualHandlingMSKQuestionnaireSection";
import { useEmployeeDateOfBirthFromHealthSurveillanceQuestionnaire } from "./useEmployeeDateOfBirthFromHealthSurveillanceQuestionnaire";
import { HealthSurveillanceConsultationGripStrengthSection } from "./HealthSurveillanceConsultationGripStrengthSection";
import { HealthSurveillanceConsultationBideltoidMeasurementSection } from "./HealthSurveillanceConsultationBideltoidMeasurementSection";
import { HealthSurveillanceConsultationOtherSection } from "./HealthSurveillanceConsultationOtherSection";
import { HealthSurveillanceConsultationChesterStepSection } from "./HealthSurveillanceConsultationChesterStepSection";
import { HealthSurveillanceConsultationSpirometrySectionOld } from "./HealthSurveillanceConsultationSpirometrySectionOld";

export const HealthSurveillanceConsultationKey = "HealthSurveillanceConsultation";
export const HealthSurveillanceRepeatMedicalConsultationKey = "HealthSurveillanceRepeatMedicalConsultationKey";

export type QuestionnaireKey = typeof HealthSurveillanceConsultationKey | typeof HealthSurveillanceRepeatMedicalConsultationKey;

type Props = {
    onDeleteFile?: (fileId: string, fileName: string, ohCaseDocumentId: string) => Promise<void>;
    getDownloadLink?: (fileAnswer: FileAnswer) => string | null;
    getQuestionnaire: (abort: AbortController) => Promise<AnsweredQuestionWithId[]>;
    loadMedicalTypesForConsultation?: () => Promise<void>;
    loadCustomMedicalTypes: () => Promise<void>;
    submitQuestionnaire: (isHavsTierThreeOrFour: boolean) => Promise<boolean>;
    saveQuestionnaire: (questions: AnsweredQuestionWithId[], eTag: string) => Promise<any>;
    submitInvalidQuestionnaire: () => void;
    navigateToNextPage: () => void;
    fileUploadApi: string;
    questionnaireKey: QuestionnaireKey;
    medicalTypesForConsultation: MedicalTypeId[] | undefined;
    customMedicalTypes: CustomMedicalType[] | undefined;
    task: TaskModel | undefined;
    readonly: boolean;
    appointmentId?: string;
    customerId: string;
    ohCaseId: string;
}

export const HealthSurveillanceConsultation: React.FunctionComponent<Props> = ({
    customMedicalTypes,
    medicalTypesForConsultation,
    loadCustomMedicalTypes,
    loadMedicalTypesForConsultation,
    submitQuestionnaire: submitQuestionnaireProp,
    getQuestionnaire,
    navigateToNextPage,
    submitInvalidQuestionnaire,
    saveQuestionnaire,
    task,
    readonly,
    customerId,
    ohCaseId,
    ...props
}) => {
    const dispatch = useDispatch();

    const enableHealthSurveillanceCrBugs = getFeatureToggles()[FeatureToggleNames.HealthSurveillanceCrBugs];

    const employeeDob = useEmployeeDateOfBirthFromHealthSurveillanceQuestionnaire(task?.customer?.id, task?.caseId);

    const [
        previousShowHealthSurveillanceConsultationExaminationSection,
        setPreviousHealthSurveillanceConsultationExaminationSection] = React.useState<boolean | null>(null);
    const [
        showHealthSurveillanceConsultationExaminationSection,
        setHealthSurveillanceConsultationExaminationSection] = React.useState<boolean>(false);

    const submitQuestionnaire = React.useCallback(async (submittedQuestions: AnsweredQuestionWithId[]): Promise<boolean> => {
        const trimAnswerAndUpdateEmployeeQuestion = (questionId: string, questions: AnsweredQuestionWithId[]) => {

            const question = questions.find(q => q.id === questionId);

            if (question) {
                question.answer = question.answer?.toString()?.trim();
            }
        };

        trimAnswerAndUpdateEmployeeQuestion(questionIds.Title, submittedQuestions);
        trimAnswerAndUpdateEmployeeQuestion(questionIds.FirstName, submittedQuestions);
        trimAnswerAndUpdateEmployeeQuestion(questionIds.LastName, submittedQuestions);

        if (submittedQuestions.some(q => q.isValid !== true && q.isRemovedByUser !== true)) {
            submitInvalidQuestionnaire();
            return false;
        } else {
            const isHavsTierThreeOrFour =
                getAnswerToQuestionWithId(submittedQuestions, HavsTierQuestionId) === HAVS_TIERS.TierThree ||
                getAnswerToQuestionWithId(submittedQuestions, HavsTierQuestionId) === HAVS_TIERS.TierFour;

            const submitted = await submitQuestionnaireProp(isHavsTierThreeOrFour);

            navigateToNextPage();
            return submitted;
        }
    }, [navigateToNextPage, submitInvalidQuestionnaire, submitQuestionnaireProp]);

    const [questions, setQuestions] = React.useState<AnsweredQuestionWithId[] | null>(null);

    const loadQuestionnaire = React.useCallback(async (abort: AbortController) => {
        setQuestions(null);
        const result = await getQuestionnaire(abort);
        setQuestions(result);
        if (!readonly) {
            const tasks = [loadCustomMedicalTypes()];

            if (loadMedicalTypesForConsultation) {
                tasks.push(loadMedicalTypesForConsultation());
            }

            await Promise.all(tasks);
        }
    }, [getQuestionnaire, loadCustomMedicalTypes, loadMedicalTypesForConsultation, readonly]);

    const questionConfigs = React.useMemo<CustomQuestionConfig[] | null | undefined>(() => {
        if (readonly === true) {
            return questions?.map(q => ({ id: q.id, questionText: q.question }));
        } else if (readonly === false && medicalTypesForConsultation && customMedicalTypes) {
            return questionConfigsForConsultationSelector(medicalTypesForConsultation, customMedicalTypes);
        }

        return null;
    }, [readonly, medicalTypesForConsultation, customMedicalTypes, questions]);

    const healthSurveillanceConsultation = useSelector((state: ApplicationState) => state.questionnaires[props.questionnaireKey]);
    const havsQuestions = values(healthSurveillanceConsultation?.sections?.find(s => s.id === sections.Havs.id)?.questions || {});
    const havsSection = healthSurveillanceConsultation?.sections?.find(s => s.id === sections.Havs.id);
    const havsTierQuestion = havsSection?.questions[HavsTierQuestionId];

    React.useEffect(() => {
        const havsExaminationSection = healthSurveillanceConsultation?.sections?.find(s => s.id === sections.Examination.id);

        if (havsTierQuestion &&
            values(havsExaminationSection?.questions || {}).length > 0)
        {
            const havsTierQuestionString = havsTierQuestion.answer as string;
            const isOldVersion = (readonly && checkExistenceOfQuestionWithId(havsQuestions, HavsAssessmentRequiredQuestionId));

            const showExaminationSection = isOldVersion ||
                havsTierQuestionString === HAVS_TIERS.TierThree ||
                havsTierQuestionString === HAVS_TIERS.TierFour;

            setHealthSurveillanceConsultationExaminationSection(showExaminationSection);
        }
    }, [havsQuestions, havsTierQuestion, healthSurveillanceConsultation?.sections, readonly]);

    const showHideExaminationSectionQuestions = React.useCallback((): void => {
        if (previousShowHealthSurveillanceConsultationExaminationSection === null ||
            previousShowHealthSurveillanceConsultationExaminationSection !==
            showHealthSurveillanceConsultationExaminationSection) {

            const examinationQuestions = values(
                healthSurveillanceConsultation?.sections?.find(s => s.id === sections.Examination.id)?.questions || {});

            if (examinationQuestions.length > 0) {
                setPreviousHealthSurveillanceConsultationExaminationSection(showHealthSurveillanceConsultationExaminationSection);

                examinationQuestions
                    .map(question => {
                        const payload = {
                            sectionId: sections.Examination.id,
                            id: question.id,
                            question: {
                                ...question,
                                isRemovedByUser: !showHealthSurveillanceConsultationExaminationSection
                            }
                        };
                        dispatch(updateQuestion({ ...payload, questionnaireKey: props.questionnaireKey }));
                    });
            }
        }
    }, [
        dispatch,
        healthSurveillanceConsultation?.sections,
        previousShowHealthSurveillanceConsultationExaminationSection,
        props.questionnaireKey,
        showHealthSurveillanceConsultationExaminationSection]
    );

    const removeAppointment = React.useCallback(async () => {
        if (task) {
            await new TasksApi().removeAppointment(task.caseId, task.id);
        }
    }, [task]);

    React.useEffect(() => {
        showHideExaminationSectionQuestions();
    }, [showHealthSurveillanceConsultationExaminationSection, showHideExaminationSectionQuestions]);

    setTimeout(() => showHideExaminationSectionQuestions(), 2000);

    return (
        <>
            {(!readonly && !props.appointmentId) && <Container textAlign="right" className="dna-button-container">
                <DidNotAttendButton customerId={customerId} ohCaseId={ohCaseId} task={task} />
            </Container>}
            {readonly !== null && <CompletedDate readonly={readonly} task={task} />}
            <ContextAwareProgressBarQuestionnaireContainer
                readonly={readonly === true}
                loadQuestionnaire={loadQuestionnaire}
                submitQuestionnaire={submitQuestionnaire}
                questionnaireKey={props.questionnaireKey}
                questionnaireType={QuestionnaireTypes.ClinicalNotes}
                removeAppointment={removeAppointment}
                autoSaveQuestionnaire={saveQuestionnaire}
            >
                {
                    questionConfigs && (
                        <>
                            <HealthSurveillanceConsultationConfidentialitySection questionConfigs={questionConfigs} questionnaireKey={props.questionnaireKey} />
                            <HealthSurveillanceConsultationHealthQuestionsSection questionConfigs={questionConfigs} questionnaireKey={props.questionnaireKey} />
                            <HealthSurveillanceConsultationDrugAlcoholTestSection questionConfigs={questionConfigs} questionnaireKey={props.questionnaireKey} />
                            <HealthSurveillanceConsultationUrinalysisSection questionConfigs={questionConfigs} questionnaireKey={props.questionnaireKey} />
                            <HealthSurveillanceConsultationMsdAssessmentSection questionConfigs={questionConfigs} questionnaireKey={props.questionnaireKey} />
                            <HealthSurveillanceConsultationSkinAssessmentSection
                                questionConfigs={questionConfigs}
                                questionnaireKey={props.questionnaireKey}
                                medicalTypes={medicalTypesForConsultation} />
                            <HealthSurveillanceConsultationHavsSection questionConfigs={questionConfigs} questionnaireKey={props.questionnaireKey} />
                            <HealthSurveillanceConsultationExaminationSection questionConfigs={questionConfigs} questionnaireKey={props.questionnaireKey} />
                            <HealthSurveillanceConsultationAudiometrySection
                                questionConfigs={questionConfigs}
                                employeeDob={employeeDob}
                                questionnaireKey={props.questionnaireKey} />
                            {enableHealthSurveillanceCrBugs
                                ? <HealthSurveillanceConsultationSpirometrySection
                                    questionConfigs={questionConfigs}
                                    employeeDob={employeeDob}
                                    questionnaireKey={props.questionnaireKey} />
                                : <HealthSurveillanceConsultationSpirometrySectionOld
                                    questionConfigs={questionConfigs}
                                    employeeDob={employeeDob}
                                    questionnaireKey={props.questionnaireKey} />
                            }
                            <HealthSurveillanceConsultationVisionSection
                                questionConfigs={questionConfigs}
                                employeeDob={employeeDob}
                                questionnaireKey={props.questionnaireKey} />
                            <HealthSurveillanceConsultationBiologicalMonitoringSection questionConfigs={questionConfigs}
                                questionnaireKey={props.questionnaireKey} />
                            <HealthSurveillanceConsultationManualHandlingMSKQuestionnaireSection
                                questionConfigs={questionConfigs}
                                questionnaireKey={props.questionnaireKey} />
                            <HealthSurveillanceConsultationChesterStepSection
                                questionConfigs={questionConfigs} />
                            <HealthSurveillanceConsultationOtherSection
                                questionConfigs={questionConfigs}
                                fileUploadApi={props.fileUploadApi}
                                onDeleteFile={props.onDeleteFile}
                                getDownloadLink={props.getDownloadLink} />
                            <HealthSurveillanceConsultationGeneralClinicNotesSection
                                questionConfigs={questionConfigs}
                                fileUploadApi={props.fileUploadApi}
                                onDeleteFile={props.onDeleteFile}
                                getDownloadLink={props.getDownloadLink}
                                questionnaireKey={props.questionnaireKey}
                            />
                            <HealthSurveillanceConsultationGripStrengthSection
                                questionConfigs={questionConfigs}
                            />
                            <HealthSurveillanceConsultationBideltoidMeasurementSection
                                questionConfigs={questionConfigs}
                            />
                        </>
                    )
                }
            </ContextAwareProgressBarQuestionnaireContainer>
        </>
    );
};
